import { useMemo } from "react"
import { useAppSelector } from "integrations/redux/hooks"
import { ContentSortDirection, ContentSortMode, ID } from "@nova/constants"
import { TagExtended } from "@nova/schemas"
import {
    selectIsSearchingGlobally,
    selectNewestProjectId,
    selectNewestTagId,
    selectSearchContents,
    selectActiveWorkspace,
} from "../WorkspaceManagementSlice"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import { selectAllFoldersById, selectFavoritesData, selectActiveProjectIds } from "./selectors"
import { useWorkspaceExplorerMode } from "panels/routing/WorkspaceExplorerRoutes"
import { useActiveFolder } from "./navigation"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"

export const useWorkspaceBodyContent = () => {
    const allFoldersById = useAppSelector(selectAllFoldersById)
    const allProjectsById = useAppSelector(selectAllFires)
    const activeWorkspace = useAppSelector(selectActiveWorkspace())
    const sortMode = useLocalStorageValue("workspace-content-sort-mode")
    const sortDirection = useLocalStorageValue("workspace-content-sort-dir")
    const searchContents = useAppSelector(selectSearchContents())
    const isSearchingGlobally = useAppSelector(selectIsSearchingGlobally())
    const newestProjectId = useAppSelector(selectNewestProjectId())
    const newestFolderId = useAppSelector(selectNewestTagId())
    const { sortedFavoriteFolderIds, sortedFavoriteProjectIds, sortedRecentProjectIds } =
        useAppSelector(selectFavoritesData)
    const activeProjectIds = useAppSelector(selectActiveProjectIds)
    const activeFolder = useActiveFolder()
    const workspaceExplorerMode = useWorkspaceExplorerMode()

    const projectsInThisFolder = activeFolder
        ? activeFolder.projects
        : activeWorkspace?.rootLevelProjects

    return useMemo(() => {
        let bodyFolderIds: ID[]
        let bodyProjectIds: ID[]

        if (searchContents && isSearchingGlobally) {
            bodyFolderIds = Object.keys(allFoldersById)
            bodyProjectIds = Object.keys(allProjectsById)
        } else {
            switch (workspaceExplorerMode) {
                case "favorites":
                    bodyFolderIds = sortedFavoriteFolderIds
                    bodyProjectIds = sortedFavoriteProjectIds
                    break
                case "none":
                case "recents":
                    bodyFolderIds = []
                    bodyProjectIds = sortedRecentProjectIds
                    break
                case "activity":
                    bodyFolderIds = []
                    bodyProjectIds = []
                    break
                case "workspace": {
                    const findInTree = (
                        tags: TagExtended[],
                        id: string
                    ): TagExtended | undefined => {
                        for (const tag of tags) {
                            if (tag.id === id) return tag
                            const found = findInTree(tag.subTags, id)
                            if (found) return found
                        }
                    }
                    const activeFolderInTree =
                        activeFolder && activeWorkspace?.tags
                            ? findInTree(activeWorkspace.tags, activeFolder.id)
                            : undefined
                    bodyFolderIds =
                        (activeFolderInTree
                            ? activeFolderInTree.subTags.map((f) => f.id)
                            : activeWorkspace?.tags.map((f) => f.id)) ?? []
                    bodyProjectIds = projectsInThisFolder?.map((p) => p.id) ?? []
                    break
                }
            }
        }

        if (searchContents) {
            bodyFolderIds = bodyFolderIds.filter((id) =>
                allFoldersById[id]?.name.toLowerCase().includes(searchContents.toLowerCase())
            )
            bodyProjectIds = bodyProjectIds.filter((id) =>
                allProjectsById[id]?.name.toLowerCase().includes(searchContents.toLowerCase())
            )
        }

        bodyFolderIds = [...bodyFolderIds].sort((a, b) => {
            const [fa, fb] = [allFoldersById[a]!, allFoldersById[b]!]
            switch (sortMode) {
                case ContentSortMode.Name:
                    return fa.name.localeCompare(fb.name)
                case ContentSortMode.DateModified:
                    return (fa.updatedAt || "").localeCompare(fb.updatedAt || "")
                case ContentSortMode.DateCreated:
                    return (fa.createdAt || "").localeCompare(fb.createdAt || "")
            }
        })

        bodyProjectIds = [...bodyProjectIds].sort((a, b) => {
            const [pa, pb] = [allProjectsById[a], allProjectsById[b]]
            switch (sortMode) {
                case ContentSortMode.Name:
                    return (pa?.name || "").localeCompare(pb?.name || "")
                case ContentSortMode.DateModified:
                    return (pa?.updatedAt || "").localeCompare(pb?.updatedAt || "")
                case ContentSortMode.DateCreated:
                    return (pa?.createdAt || "").localeCompare(pb?.createdAt || "")
            }
        })

        const sortedActiveProjectIds = [...activeProjectIds].sort((a, b) =>
            (allProjectsById[a]?.updatedAt || "").localeCompare(allProjectsById[b]?.updatedAt || "")
        )

        const newestProjectIndex = newestProjectId ? bodyProjectIds.indexOf(newestProjectId) : -1
        if (newestProjectIndex > -1) {
            bodyProjectIds.splice(newestProjectIndex, 1)
            if (sortDirection === ContentSortDirection.Descending)
                bodyProjectIds.push(newestProjectId!)
            else bodyProjectIds.unshift(newestProjectId!)
        }

        const newestFolderIndex = newestFolderId ? bodyFolderIds.indexOf(newestFolderId) : -1
        if (newestFolderIndex > -1) {
            bodyFolderIds.splice(newestFolderIndex, 1)
            if (sortDirection === ContentSortDirection.Descending)
                bodyFolderIds.push(newestFolderId!)
            else bodyFolderIds.unshift(newestFolderId!)
        }

        if (sortDirection === ContentSortDirection.Descending) {
            bodyFolderIds.reverse()
            bodyProjectIds.reverse()
        }

        return { bodyFolderIds, bodyProjectIds, activeProjectIds: sortedActiveProjectIds }
    }, [
        searchContents,
        isSearchingGlobally,
        allProjectsById,
        newestProjectId,
        newestFolderId,
        sortDirection,
        allFoldersById,
        workspaceExplorerMode,
        sortedFavoriteFolderIds,
        sortedFavoriteProjectIds,
        sortedRecentProjectIds,
        activeFolder,
        activeWorkspace?.tags,
        projectsInThisFolder,
        sortMode,
        activeProjectIds,
    ])
}
